/* Capa 2 · la ventanilla: un día. Arrastrar el paisaje (o el riel) mueve el tiempo; el cartel
   dice qué toca a esa hora. Lo que cambia a cada fotograma se toca directamente en el DOM. */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { estadoDe, marcaDe, useApuntes } from '../datos/apuntes.ts';
import { peligro } from '../datos/retraso.ts';
import { lluvia, useTiempo } from '../datos/meteo.ts';
import type { Dia, Viaje } from '../datos/tipos.ts';
import { bloqueEn as bloqueA, type BloqueV } from '../escena/bloques.ts';
import { KANJI_HITO } from '../escena/dibujo.ts';
import { reducido, type Motor } from '../escena/motor.ts';
import { dur, fechaCorta, fechaLarga, hhmm, kanjiDe, num2 } from '../texto.ts';
import { ahoraMin, useTic } from '../tiempo.ts';
import { useUbicacion } from '../datos/ubicacion.ts';

interface Props {
  motor: Motor;
  v: Viaje;
  dia: Dia;
  total: number;
  activa: boolean;
  esHoy: boolean;
  tamano: string;
  onTren: () => void;
  onDia: (n: number) => void;
  onParada: (b: BloqueV) => void;
  onHoja: (h: 'dia' | 'billetes' | 'gasto' | 'mapa') => void;
  onGuia: () => void;
  onFrases: () => void;
  onCuanto: () => void;
  bloqueado: boolean;                 // hay una hoja abierta: el teclado es suyo
}

/** Toque: `corto`. Mantener medio segundo: `largo` (y el toque de después no cuenta).
    Con refs: entre pulsar y soltar la ventanilla se vuelve a pintar (se abre la guía). */
function usePulsarLargo(corto: () => void, largo: () => void) {
  const t = useRef(0), hecho = useRef(false);
  const soltar = () => clearTimeout(t.current);
  return {
    onPointerDown: () => { hecho.current = false; t.current = window.setTimeout(() => { hecho.current = true; navigator.vibrate?.(15); largo(); }, 500); },
    onPointerUp: soltar, onPointerLeave: soltar, onPointerCancel: soltar,
    onContextMenu: (e: { preventDefault: () => void }) => e.preventDefault(),
    onClick: () => { if (!hecho.current) corto(); hecho.current = false; },
  };
}

export function Ventana({ motor, v, dia, total, activa, esHoy, tamano, onTren, onDia, onParada, onHoja, onGuia, onFrases, onCuanto, bloqueado }: Props) {
  useApuntes();
  useTic(30_000);
  const pildora = usePulsarLargo(onTren, onGuia);
  const compartiendo = useUbicacion().yo !== null;
  const meteo = useTiempo(v);
  const [rango, setRango] = useState({ pMin: 0, pMax: 1440, bl: [] as BloqueV[] });
  const [cartel, setCartel] = useState({ i: -1, dir: 0 });
  const idx = useRef(-1);
  const hora = useRef<HTMLDivElement>(null), sub = useRef<HTMLDivElement>(null), volver = useRef<HTMLButtonElement>(null);
  const riel = useRef<HTMLDivElement>(null), cursor = useRef<HTMLSpanElement>(null), cara = useRef<HTMLSpanElement>(null);
  const ant = useRef<HTMLButtonElement>(null), sig = useRef<HTMLButtonElement>(null), anuncio = useRef<HTMLParagraphElement>(null);

  const pct = (m: number) => `${(((m - rango.pMin) / (rango.pMax - rango.pMin)) * 100).toFixed(2)}%`;

  // El bloque a una hora: el que la contiene (sin contar los saltados), o el siguiente
  const bloqueEn = (p: number, bl = motor.bl) => bloqueA(bl, p, x => estadoDe(x.id) === 'saltado');

  // Construir la escena del día al entrar (y al cambiar de día o de tamaño)
  useLayoutEffect(() => {
    if (!activa) return;
    const p = motor.dia === dia && motor.modo === 'ventana' ? motor.p : null;
    motor.modo = 'ventana';
    motor.construir(dia);
    // Hoy, a la hora de ahora; otro día, desde su primera parada
    motor.p = motor.limitar(p ?? (esHoy ? ahoraMin() : motor.bl[0].t.ini));
    idx.current = -1;
    setRango({ pMin: motor.pMin, pMax: motor.pMax, bl: motor.bl });
    motor.pintar(motor.p);
    document.title = `JP${num2(dia.n)} · ${dia.titulo} · Tabiji`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activa, dia, tamano]);

  // Lo que cambia a cada fotograma
  useEffect(() => {
    if (!activa || !rango.bl.length) return;
    const alMover = (p: number) => {
      if (hora.current) hora.current.textContent = hhmm(p);
      if (sub.current && volver.current) {
        if (esHoy) {
          const d = Math.round(p - ahoraMin());
          sub.current.textContent = Math.abs(d) <= 2 ? 'AHORA' : d > 0 ? `DENTRO DE ${dur(d).toUpperCase()}` : `HACE ${dur(-d).toUpperCase()}`;
          volver.current.hidden = Math.abs(d) <= 5;
        } else {
          sub.current.textContent = fechaLarga(dia).toUpperCase();
          volver.current.hidden = true;
        }
      }
      if (cursor.current) cursor.current.style.left = pct(p);
      // Si a esa hora se espera lluvia, llueve en el paisaje (solo decorado: el texto está en la hoja del día)
      const app = document.querySelector<HTMLElement>('.app'), cuanto = lluvia(meteo, dia, p);
      app?.toggleAttribute('data-llueve', cuanto > 0);
      app?.style.setProperty('--lluvia', cuanto.toFixed(2));
      const b = bloqueEn(p, rango.bl);
      riel.current?.setAttribute('aria-valuenow', String(Math.round(p)));
      riel.current?.setAttribute('aria-valuetext', `${hhmm(p)}, ${b.que}`);
      if (b.i !== idx.current) {
        const dir = idx.current < 0 ? 0 : Math.sign(b.i - idx.current);
        idx.current = b.i;
        setCartel({ i: b.i, dir });
      }
      if (ant.current) ant.current.disabled = !rango.bl.some(x => x.mid < p - 1);
      if (sig.current) sig.current.disabled = !rango.bl.some(x => x.mid > p + 1);
    };
    motor.alMover = alMover;
    alMover(motor.p);
    return () => { if (motor.alMover === alMover) motor.alMover = () => {}; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activa, rango, esHoy, meteo]);

  // El cartel se da la vuelta como un letrero de estación al cambiar de parada
  useLayoutEffect(() => {
    if (cartel.dir && !reducido()) cara.current?.animate([{ transform: `rotateX(${cartel.dir > 0 ? -85 : 85}deg)`, opacity: 0.3 }, { transform: 'rotateX(0)', opacity: 1 }], { duration: 380, easing: 'cubic-bezier(.2,.9,.2,1)' });
  }, [cartel]);

  const saltar = (dir: number) => {
    const b = dir > 0 ? rango.bl.find(x => x.mid > motor.p + 1) : [...rango.bl].reverse().find(x => x.mid < motor.p - 1);
    if (b) { motor.irA(b.mid); if (anuncio.current) anuncio.current.textContent = `${b.t.iniTxt}, ${b.que}`; }
  };

  // Teclado: flechas de parada en parada, Esc vuelve al tren
  useEffect(() => {
    if (!activa || bloqueado) return;
    const tecla = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).closest?.('input, textarea, select, .riel')) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); saltar(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); saltar(-1); }
      else if (e.key === 'Escape') onTren();
    };
    document.addEventListener('keydown', tecla);
    return () => document.removeEventListener('keydown', tecla);
  });

  // El riel también se arrastra
  const enRiel = useRef(false);
  const pDeRiel = (x: number) => { const r = riel.current!.getBoundingClientRect(); return rango.pMin + ((x - r.left) / r.width) * (rango.pMax - rango.pMin); };

  const b = cartel.i >= 0 ? rango.bl[cartel.i] : null;
  const estado = b ? estadoDe(b.id) : undefined;
  // Si con lo marcado peligra una hora fija, el cartel lo dice hasta llegar a ella
  const pel = esHoy ? peligro(rango.bl, dia.n, marcaDe) : null;
  const [clase, txt] = useMemo((): [string, string] => {
    if (!b) return ['', ''];
    if (estado === 'hecho') return ['hecho', '済 hecho'];
    if (estado === 'saltado') return ['saltado', 'saltado'];
    if (!esHoy) return ['', ''];
    if (pel && b.t.ini <= pel.ancla.t.ini && b.t.fin > ahoraMin() - 60) return ['peligro', `▲ ${pel.ancla.t.iniTxt} peligra`];
    const now = ahoraMin();
    if (b.t.ini <= now && now < b.t.fin) return ['ahora', 'ahora'];
    return b.t.ini > now ? ['', `en ${dur(b.t.ini - now)}`] : ['', 'ya pasó'];
  }, [b, estado, esHoy, pel]);

  return (
    <section className="ventana-ui" id="ventana" aria-label="El día por la ventanilla" inert={!activa}>
      <div className="barra">
        <div className="barra__izq">
          <button className="pildora" type="button" aria-label="Volver al tren" aria-description="Mantén pulsado para abrir la guía del viaje" {...pildora}>
            <span className="num">JP{num2(dia.n)}</span><span>{kanjiDe(dia)} · {fechaCorta(dia)}</span>
          </button>
          {/* Las frases, siempre a mano: para enseñarlas, escucharlas o traducir una nueva */}
          <button className="pildora pildora--frases" type="button" onClick={onFrases}><span lang="ja" aria-hidden="true">言葉</span>Frases</button>
          {/* ¿Cuánto es en euros? A un toque, para las etiquetas */}
          <button className="pildora pildora--frases pildora--cuanto" type="button" onClick={onCuanto}><span lang="ja" aria-hidden="true">¥</span>¿Cuánto es?</button>
        </div>
        <div className="barra__dias">
          <button className="pildora pildora--icono" type="button" aria-label="Día anterior" disabled={dia.n === 1} onClick={() => onDia(dia.n - 1)}>‹</button>
          <button className="pildora pildora--icono" type="button" aria-label="Día siguiente" disabled={dia.n === total} onClick={() => onDia(dia.n + 1)}>›</button>
        </div>
      </div>
      <div className="reloj" aria-hidden="true">
        <div className="reloj__hora" ref={hora} />
        <div className="reloj__sub" ref={sub} />
        <button className="volver-ahora" type="button" ref={volver} hidden onClick={() => motor.irA(ahoraMin())}>AHORA</button>
      </div>
      <div />
      <div className="abajo">
        <div className="cartel-fila">
          <button className="flecha" type="button" aria-label="Parada anterior" ref={ant} onClick={() => saltar(-1)}>‹</button>
          <button
            className="cartel"
            type="button"
            aria-live="polite"
            data-real={estado ?? ''}
            aria-label={b ? `${b.que}, de ${b.t.iniTxt} a ${b.t.finTxt}. Prioridad ${b.pri}. ${txt}. Abrir detalle` : undefined}
            onClick={() => b && onParada(bloqueEn(motor.p))}
          >
            {b && (
              <span className="cartel__cara" ref={cara}>
                <span className="cartel__pri" data-pri={b.pri}>{b.pri}</span>
                <span style={{ minWidth: 0, display: 'block' }}>
                  <span className="cartel__hora">{b.t.iniTxt} – {b.t.finTxt}{txt && <> · <span className={`estado ${clase ? `estado--${clase}` : ''}`}>{txt}</span></>}</span>
                  <span className="cartel__nombre" style={{ display: 'block' }}>{b.que}</span>
                </span>
                <span className="cartel__marca" lang="ja" aria-hidden="true">{(b.hito && KANJI_HITO[b.hito]) ?? '旅'}</span>
                {estado === 'hecho' && <span className="cartel__sello" lang="ja" aria-hidden="true">済</span>}
              </span>
            )}
          </button>
          <button className="flecha" type="button" aria-label="Parada siguiente" ref={sig} onClick={() => saltar(1)}>›</button>
        </div>
        <div
          className="riel"
          ref={riel}
          role="slider"
          tabIndex={0}
          aria-label="Hora del día"
          aria-valuemin={0}
          aria-valuemax={1440}
          onPointerDown={e => { motor.parar(); enRiel.current = true; riel.current!.setPointerCapture(e.pointerId); motor.irA(pDeRiel(e.clientX), 350); }}
          onPointerMove={e => { if (enRiel.current) { motor.parar(); motor.p = motor.limitar(pDeRiel(e.clientX)); motor.redibujar(); } }}
          onPointerUp={() => { enRiel.current = false; }}
          onKeyDown={e => {
            const d = ({ ArrowRight: 15, ArrowLeft: -15, PageUp: 60, PageDown: -60 } as Record<string, number>)[e.key];
            if (d) { e.preventDefault(); motor.parar(); motor.p = motor.limitar(motor.p + d); motor.redibujar(); }
          }}
        >
          {rango.bl.map(x => (
            <span key={x.id} className="riel__tramo" data-pri={x.pri} data-real={estadoDe(x.id) ?? ''} data-examen={x.examen ? '' : undefined}
              style={{ left: pct(x.t.ini), width: `calc(${pct(Math.min(x.t.fin, x.t.ini + 300))} - ${pct(x.t.ini)} - 2px)` }} />
          ))}
          {esHoy && <span className="riel__ahora" style={{ left: pct(Math.min(rango.pMax, Math.max(rango.pMin, ahoraMin()))) }} />}
          <span className="riel__cursor" ref={cursor} />
        </div>
        <nav className="dock" aria-label="Acciones">
          <button type="button" onClick={() => onHoja('dia')}><span lang="ja" aria-hidden="true">一日</span>Día</button>
          <button type="button" onClick={() => onHoja('mapa')}><span lang="ja" aria-hidden="true">地図</span>Mapa{compartiendo && <i className="dock__punto" role="img" aria-label="Estás compartiendo tu ubicación" />}</button>
          <button type="button" onClick={() => onHoja('billetes')}><span lang="ja" aria-hidden="true">切符</span>Billetes</button>
          <button type="button" className="principal" onClick={() => onHoja('gasto')}><span lang="ja" aria-hidden="true">¥</span>Gasto</button>
        </nav>
      </div>
      <p className="oculto" aria-live="polite" ref={anuncio} />
    </section>
  );
}
